import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Server, Database, Cloud, Cpu, Network, Layers, Cable, Video, ArrowRight } from 'lucide-react';

const SystemIntegration = () => {
  const services = [
    {
      icon: Server,
      title: 'IT Infrastructure',
      description: 'Complete data center solutions with support for leading vendors like HPE, Dell, Cisco, and Nutanix'
    },
    {
      icon: Database,
      title: 'Enterprise Applications',
      description: 'HRMS, ERP, CRM, and Accounting solutions including Office 365 and cloud-based platforms'
    },
    {
      icon: Cloud,
      title: 'Cloud Services (IaaS)',
      description: 'Scalable cloud infrastructure, managed storage solutions, and hybrid cloud deployment'
    },
    {
      icon: Network,
      title: 'Network Security',
      description: 'Enterprise-grade security solutions from Cisco, Fortinet, Palo Alto, and other leading vendors'
    },
    {
      icon: Layers,
      title: 'Data Engineering & BI',
      description: 'Business intelligence dashboards, data science, predictive analytics, and data warehousing'
    },
    {
      icon: Cable,
      title: 'Structured Cabling',
      description: 'Professional structured cabling infrastructure for data centers and enterprise facilities'
    },
    {
      icon: Video,
      title: 'ELV Solutions',
      description: 'CCTV surveillance, access control systems, fire alarm systems, and public address systems'
    },
    {
      icon: Cpu,
      title: 'Smart Infrastructure',
      description: 'IoT-enabled smart city solutions, intelligent building management, and automation systems'
    }
  ];

  const solutions = [
    {
      title: 'Digital Network Design and Implementation',
      description: 'End-to-end network design, deployment, and optimization for enterprise environments',
      items: ['Network architecture design', 'Router and switch configuration', 'Wireless network deployment', 'Network security implementation'],
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop'
    },
    {
      title: 'Data Life Cycle Solutions',
      description: 'Comprehensive data management from creation to archival and secure disposal',
      items: ['Data backup and recovery', 'Storage optimization', 'Data archival solutions', 'Disaster recovery planning'],
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&h=400&fit=crop'
    },
    {
      title: 'Smart City / Safe City',
      description: 'Integrated ICT solutions for modern urban infrastructure and public safety',
      items: ['Intelligent surveillance systems', 'Traffic management solutions', 'Emergency response systems', 'Citizen services platforms'],
      image: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=600&h=400&fit=crop'
    },
    {
      title: 'Managed Services',
      description: '24/7 monitoring, maintenance, and support for your IT infrastructure',
      items: ['Infrastructure monitoring', 'Proactive maintenance', 'Help desk support', 'Performance optimization'],
      image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=600&h=400&fit=crop'
    }
  ];

  const vendors = [
    'HPE', 'Dell EMC', 'Cisco', 'Nutanix', 'VMware', 'Microsoft',
    'Fortinet', 'Palo Alto', 'Check Point', 'Sophos', 'Veeam', 'NetApp'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1920&h=800&fit=crop" 
            alt="System Integration" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Server className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-blue-300 animate-float" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">System Integration Services</h1>
            <p className="text-lg md:text-xl text-blue-100 mb-8">
              Turnkey ICT projects and smart infrastructure solutions that transform your business operations
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105">
                Discuss Your Project
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Integration Solutions</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Our best-of-breed solutions help you stay ahead of the competition and maximize your business growth. With expertise in networking, security, servers, and storage, we enable swift and efficient operations scaling.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardHeader>
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Details */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Solution Offerings</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {solutions.map((solution, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={solution.image} 
                    alt={solution.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-2xl font-bold text-white">{solution.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-6">{solution.description}</p>
                  <div className="space-y-3">
                    {solution.items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        </div>
                        <span className="text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Partners */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Partners</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 mb-8">We partner with industry-leading vendors to deliver best-in-class solutions</p>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-8 max-w-5xl mx-auto">
            {vendors.map((vendor, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg flex items-center justify-center hover:shadow-md transition-shadow">
                <span className="text-lg font-semibold text-gray-700">{vendor}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Why Choose Our Integration Services?</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-12"></div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Turnkey Solutions</h3>
                <p className="text-gray-600">
                  From design to deployment and ongoing support, we handle every aspect of your IT infrastructure project.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Vendor Agnostic</h3>
                <p className="text-gray-600">
                  We recommend the best solutions for your needs, not tied to any single vendor's products.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Future-Ready</h3>
                <p className="text-gray-600">
                  Our solutions are designed to scale with your business and adapt to emerging technologies.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Team</h3>
                <p className="text-gray-600">
                  Certified professionals with extensive experience in complex enterprise deployments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-950 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Infrastructure?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Let's discuss how our system integration solutions can optimize your business operations.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8">
              Start Your Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SystemIntegration;